import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import Footer from './Footer';
import EmailSignupPopup from '@/components/EmailSignupPopup';
import SocialProofToasts from '@/components/upsell/SocialProofToasts';
import ExitIntentPopup from '@/components/upsell/ExitIntentPopup';
import CartDrawer from '@/components/CartDrawer';
import AdminBar from '@/components/quickedit/AdminBar';
import PreviewBanner from '@/components/PreviewBanner';
import { useSettings } from '@/hooks/useProducts';
import { useAxissWidget } from '@/hooks/useAxissWidget';
import { useIntegrationScripts } from '@/hooks/useIntegrationScripts';
import JsonLd from '@/components/JsonLd';
import { useMaintenanceMode } from '@/hooks/useMaintenance';
import { useAuthStore } from '@/stores/auth-store';
import { BYPASS_PATHS } from '@/config/maintenance';
import { SITE_NAME, SITE_URL, IS_SOCIAL_CASINO } from '@/config/site';
import AgeGate from '@/components/AgeGate';
import InstallPrompt from '@/components/pwa/InstallPrompt';
import { useResolvedLook } from '@/looks';
import { canBypassMaintenance } from '@/lib/roles';

export default function PublicLayout() {
  const { t } = useTranslation('nav');
  const { data: settingsData } = useSettings();
  // Initialize Axiss widget and sync user identity
  useAxissWidget();
  // Connected integrations' storefront scripts (Klaviyo, ...) + identify.
  useIntegrationScripts();

  const location = useLocation();
  const { data: maintenance } = useMaintenanceMode();
  const user = useAuthStore((s) => s.user);
  const enabled = !!maintenance?.enabled;
  // Same rule as MaintenanceGuard — these two must agree or the chrome and the
  // page disagree about whether the site is up.
  const canBypass = enabled && canBypassMaintenance(user?.role);
  const isBypassPath = BYPASS_PATHS.some((p) => location.pathname.startsWith(p));
  const showHeaderFooter = !enabled || canBypass || !isBypassPath;

  // Minimal-chrome layout mode: header strips down to logo + Sign In + Play
  // Now, footer collapses to a single compliance row, and homepage popups are
  // suppressed. Seeded for social_casino tenants by the wizard's db-setup
  // block; other tenants can opt-in by setting site_settings.layout_chrome to
  // 'minimal' manually.
  const layoutChrome: string = settingsData?.settings?.layout_chrome || 'default';
  const isMinimalChrome = layoutChrome === 'minimal';

  // Age gate: shown on first visit to social_casino tenants. The modal blocks
  // page interaction until acknowledged and persists the ack to localStorage.
  // Setting toggle so non-casino tenants can opt-in too (rare).
  const ageGateEnabled: boolean = !!settingsData?.settings?.age_gate_enabled || IS_SOCIAL_CASINO;
  const ageGateMode: 'checkbox' | 'dob' =
    (settingsData?.settings?.age_gate_mode as 'checkbox' | 'dob') || 'checkbox';
  const minAge: number =
    typeof settingsData?.settings?.min_age === 'number'
      ? settingsData.settings.min_age
      : 18;

  // The tenant's selected Look (theme_config.look) resolved into concrete
  // variant choices, with the site type's default for tenants that have none
  // (e.g. lodging→hospitality_warm with a centered_logo header that keeps the
  // nav in a separate row, avoiding collision between the logo and nav items
  // on desktop). Those defaults are the same table the wizard's AI fallback
  // uses. resolveLook is defensive: bad input never throws.
  const resolvedLook = useResolvedLook();

  // The runtime theme palette + kit favicon effects moved to
  // components/RuntimeBrand.tsx (mounted once in App.tsx) so account, checkout
  // and admin routes get an applied brand kit without a redeploy too.

  // Apply Look-driven body data attributes so the global rules in
  // index.css (look radius / density / button / card / product card) can
  // target them. The default Look (cozy_boutique) emits 'soft' /
  // 'comfortable' / 'rounded' / 'elevated' / 'standard' which match the CSS
  // baseline — no overrides fire, so existing tenants render identically.
  useEffect(() => {
    const body = document.body;
    body.dataset.lookRadius = resolvedLook.radius_scale;
    body.dataset.lookDensity = resolvedLook.density;
    body.dataset.lookButton = resolvedLook.button_shape;
    body.dataset.lookCard = resolvedLook.card_style;
    body.dataset.lookProductCard = resolvedLook.product_card_variant;
    return () => {
      delete body.dataset.lookRadius;
      delete body.dataset.lookDensity;
      delete body.dataset.lookButton;
      delete body.dataset.lookCard;
      delete body.dataset.lookProductCard;
    };
  }, [
    resolvedLook.radius_scale,
    resolvedLook.density,
    resolvedLook.button_shape,
    resolvedLook.card_style,
    resolvedLook.product_card_variant,
  ]);

  return (
    <div className="min-h-screen flex flex-col">
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: settingsData?.settings?.store_name || SITE_NAME,
        url: SITE_URL,
        logo: settingsData?.settings?.store_logo_url || `${SITE_URL}/favicon.svg`,
        sameAs: [
          settingsData?.settings?.facebook_url,
          settingsData?.settings?.instagram_url,
          settingsData?.settings?.twitter_url,
          settingsData?.settings?.pinterest_url,
          settingsData?.settings?.youtube_url,
        ].filter(Boolean),
      }} />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: settingsData?.settings?.store_name || SITE_NAME,
        url: SITE_URL,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      }} />
      <a href="#main-content" className="skip-link">
        {t('skipToContent')}
      </a>
      {showHeaderFooter && (
        <Header
          minimal={isMinimalChrome}
          variant={isMinimalChrome ? 'minimal' : resolvedLook.header_variant}
        />
      )}
      <main id="main-content" className="flex-1" tabIndex={-1}>
        <Outlet />
      </main>
      {showHeaderFooter && (
        <Footer
          minimal={isMinimalChrome}
          variant={isMinimalChrome ? 'minimal' : resolvedLook.footer_variant}
        />
      )}
      {showHeaderFooter && !isMinimalChrome && (
        <>
          <EmailSignupPopup />
          <SocialProofToasts />
          <ExitIntentPopup />
          <CartDrawer />
        </>
      )}
      <AdminBar />
      <PreviewBanner />
      {ageGateEnabled && (
        <AgeGate minAge={minAge} mode={ageGateMode} />
      )}
      {/* Home-screen install banner. Self-hides when installed, dismissed, or
          when the platform offers no install path. Suppressed under minimal
          chrome (e.g. social-casino) to keep that layout uncluttered. */}
      {showHeaderFooter && !isMinimalChrome && <InstallPrompt />}
    </div>
  );
}
